import { AccessibilityType } from '../../../../redux/application/application.types';
import HighContrastButton from '../../../common/HighContrastButton/HighContrastButton';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import React from 'react';

interface AccessibilityConfigViewProps {
    config?: AccessibilityType;
}

const AccessibilityConfigView = ({ config }: AccessibilityConfigViewProps) => {
    if (!config) {
        return null;
    }

    return (
        <>
            <p className={'my-2'}>
                Diese Erklärung zur Barrierefreiheit gilt für die unter der Adresse {config?.mainUrl ?? window.location.host} veröffentlichen Webseite
                {config?.companyName && <> der {config?.companyName}</>}.
            </p>
            <p className={'my-2'}>
                Wir sind bemüht unsere Webseiten barrierefrei zugänglich zu machen. {config?.legals && <>&nbsp;Dies geschieht auf Grundlage von:</>}
            </p>
            {config?.legals && (
                <>
                    <ul className={'my-2 list-disc ml-8 list-outside'}>
                        {config?.legals.map((legal) => {
                            return <li className={'my-2 pl-4'}>{legal.text}</li>;
                        })}
                    </ul>
                </>
            )}
            <p>
                Diese Erklärung wurde am 15.04.2025 erstellt und basiert auf einer vom 03.04. bis 14.04.2025 durchgeführten Selbstbewertung durch die envago
                GmbH. Sollten Sie bei der Nutzung unseres Webangebots auf Barrieren stoßen, teilen Sie uns diese gerne mit.
            </p>

            <h2 className={'text-xl mt-6 mb-4'}>Barrierefreie Unterstützung &amp; Alternativen</h2>

            <p>
                Wir sind bemüht, die Ablesung und Übermittlung Ihrer Zählerstände möglichst einfach zu gestalten.
                {(config?.alternatives?.length ?? 0) > 0 && <> Gerne können Sie auch unsere alternativen Kanäle zur Durchführung Ihrer Ablesung nutzen:</>}
            </p>

            <section className={'ml-4'}>
                <h3 className={'text-lg mt-4 mb-2'}>Hoher Kontrast</h3>
                <p>
                    Wenn Sie in Ihren Systemeinstellungen den Modus für 'hohen Kontrast' aktiviert haben, wird diese Webseite automatisch mit besonders
                    kontrastreichen Farben dargestellt. Alternativ können Sie in der Menü-Leiste oder über diesen Button den Kontrast erhöhen:
                    <br />
                </p>

                <p className={'my-4'}>
                    <HighContrastButton smallButton={false} />
                </p>
            </section>

            {config?.alternatives?.map((alt) => {
                return (
                    <section className={'ml-4'}>
                        <h3 className={'text-lg mt-4 mb-2'}>{alt.title}</h3>
                        {alt.description && <p className={'mb-4'}>{alt.description}</p>}

                        <a href={alt.link}>{alt.text}</a>
                    </section>
                );
            })}

            <h2 className={'text-xl mt-6 mb-4'}>Stand der Umsetzung der Barrierefreiheit</h2>
            <p>
                Diese Webseite ist mit den gesetzlichen Anforderungen zur Barrierefreiheit <em className={'font-bold'}>teilweise vereinbar</em>. Folgende
                Elemente und Funktionen entsprechen nicht den Richtlinien:
                <ul className={'my-2 list-disc ml-8 list-outside'}>
                    <li className={'my-2'}>
                        Aufgrund individueller, anpassbarer Farbeinstellungen kann es zu eingeschränkten Kontrasten bei Icons und Symbolen sowie fehlerhaften
                        Farben im Modus mit hohem Kontrast kommen.
                    </li>
                    <li className={'my-2'}>ARIA-Labels sind teilweise nur in deutscher Sprache verfügbar.</li>
                    <li className={'my-2'}>Kontraste der Fokus-Indikatoren sind z.T. zu gering; im High-Contrast Modus sind diese verbessert.</li>
                    <li className={'my-2'}>Text-Beschreibungen der eingebundenen Bilder in der Hilfe sind sehr grob.</li>
                    <li className={'my-2'}>
                        Komplexe Tabellen-Elemente, wie die Funktion zum Teilen von Aufträgen - soweit für Ihren Account aktiviert - sind nur teilweise
                        barrierefrei.
                    </li>
                </ul>
                Wir sind bemüht, diese Einschränkungen, soweit technisch möglich, zu beheben.
            </p>
            <h2 className={'text-xl mt-6 mb-4'}>Kontakt</h2>
            <div>
                <p>
                    Wenn Sie auf unserer Webseite Barrieren feststellen, freuen wir uns, wenn Sie uns diese mitteilen. Hierzu bitten wir Sie, uns die konkrete
                    Seite (URL) und eine möglichst genaue Beschreibung des Fehlers zu nennen.
                    {config?.contact?.person && (
                        <>
                            <br />
                            Unser Ansprechpartner für den barrierefreien Zugang zu unserem Angebot ist {config?.contact.person.text}.
                        </>
                    )}
                </p>
                <LabeledList className={'py-4 px-4'}>
                    {config?.contact?.email && (
                        <>
                            <OptionalLabel label={'per E-Mail'}>
                                <a href={`mailto:${config?.contact.email.link}`}>{config?.contact.email.text}</a>
                            </OptionalLabel>
                        </>
                    )}

                    {config?.contact?.phone && (
                        <>
                            <OptionalLabel label={'per Telefon'}>
                                <a href={`tel:${config?.contact.phone.link}`}>{config?.contact.phone.text}</a>
                            </OptionalLabel>
                        </>
                    )}
                    {config?.contact?.postal && (
                        <>
                            <OptionalLabel label={'per Brief'}>{config?.contact.postal}</OptionalLabel>
                        </>
                    )}
                    {config?.contact?.form && (
                        <>
                            <OptionalLabel label={'per Formular'}>
                                <a href={`${config?.contact.form.link}`}>{config?.contact.form.text}</a>
                            </OptionalLabel>
                        </>
                    )}
                </LabeledList>
            </div>
            <h2 className={'text-xl mt-6 mb-4'}>Durchsetzungsverfahren</h2>
            <p className={'my-2'}>
                Sie haben unter oben genanntem Kontakt eine Anfrage zur Barrierefreiheit unserer Webseiten gestellt. Falls dabei innerhalb einer angemessenen
                Frist (in der Regel vier Wochen) keine zufriedenstellende Lösung gefunden wird, können Sie sich an die für das Durchsetzungsverfahren zuständige
                Durchsetzungsstelle wenden.
            </p>
            <p className={'my-2'}>
                Die Durchsetzungsstelle unterstützt eine außergerichtliche Streitbeilegung. Dieses Schlichtungsverfahren ist kostenlos. Die Einschaltung eines
                Rechtsbeistands ist nicht erforderlich.
            </p>

            {config?.enforcementOffice && (
                <>
                    <LabeledList className={'py-4 px-4'}>
                        <OptionalLabel label={'Durchsetzungsstelle'}>{config?.enforcementOffice.name}</OptionalLabel>
                        <OptionalLabel label={'Ansprechpartner'}>{config?.enforcementOffice.contact}</OptionalLabel>
                        <OptionalLabel label={'Postanschrift'}>{config?.enforcementOffice.postal}</OptionalLabel>
                        {config?.enforcementOffice.email && (
                            <OptionalLabel label={'E-Mail'}>
                                <a href={`mailto:${config?.enforcementOffice.email?.link}`}>{config?.enforcementOffice.email?.text}</a>
                            </OptionalLabel>
                        )}
                        {config?.enforcementOffice.phone && (
                            <OptionalLabel label={'Telefon'}>
                                <a href={`tel:${config?.enforcementOffice.phone?.link}`}>{config?.enforcementOffice.phone?.text}</a>
                            </OptionalLabel>
                        )}
                        {config?.enforcementOffice.form && (
                            <OptionalLabel label={'Formular'}>
                                <a href={`${config?.enforcementOffice.form?.link}`}>{config?.enforcementOffice.form?.text}</a>
                            </OptionalLabel>
                        )}
                        {config?.enforcementOffice.web && (
                            <OptionalLabel label={'Webseite'}>
                                <a href={`${config?.enforcementOffice.web?.link}`}>{config?.enforcementOffice.web?.text}</a>
                            </OptionalLabel>
                        )}
                    </LabeledList>
                </>
            )}
        </>
    );
};

export default AccessibilityConfigView;
